import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, X, AlertCircle } from 'lucide-react';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';

// LAB RESULT SUMMARY (Phase 4, #15 — lab pack). Plain-language explanation of a
// RELEASED report, loaded server-side from the real values. Explains, never
// diagnoses — the closing line always points back to the treating doctor.

const LabSummaryModal = ({ reportId, open, onClose }) => {
  const { t, i18n } = useTranslation();
  const [summary, setSummary] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !reportId) return;
    setSummary(''); setError(''); setLoading(true);
    (async () => {
      try {
        const axiosInstance = createAuthedAxios();
        const { data } = await axiosInstance.post(`${baseURL}/ai-packs/lab/summary`, {
          reportId,
          language: i18n.language === 'fr' ? 'French' : 'English',
        });
        setSummary(data.summary || '');
      } catch (err) {
        setError(err?.response?.data?.message || t('aiPacks.summary_failed', 'Could not explain this report'));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, reportId, i18n.language, t]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="lab-summary-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full flex flex-col max-h-[85vh]">
        <div className="border-b border-gray-100 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center">
              <Sparkles size={17} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">{t('aiPacks.summary_title', 'Report, in plain words')}</h2>
              <p className="text-[11px] text-gray-400">{reportId}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors"><X size={20} /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {loading ? (
            <div className="space-y-2.5 animate-pulse">
              {[...Array(5)].map((_, i) => <div key={i} className={`h-3.5 bg-gray-100 rounded ${i % 2 ? 'w-5/6' : 'w-full'}`} />)}
            </div>
          ) : error ? (
            <p className="text-sm text-rose-500 flex items-start gap-1.5">
              <AlertCircle size={15} className="mt-0.5 shrink-0" /> {error}
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{summary}</p>
              <p className="mt-4 text-[11px] text-amber-600 flex items-start gap-1.5">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                {t('aiPacks.summary_disclaimer', 'An AI explanation of the released values — it never replaces the doctor who ordered the test.')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LabSummaryModal;
